<template>
  <LocationGroupAutocompleteInput
    :id="id"
    v-model="selectedGroups"
    :options="locationGroups.data ?? []"
    :loading="locationGroups.loading || saving"
    :disabled="disabled"
    :errors="errors"
    @create="onCreate"
  />
</template>

<script lang="ts" setup>
import { ErrorObject } from '@vuelidate/core';
import { computed, onMounted, PropType, ref, toRefs } from 'vue';

import { LocationGroup } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { useLocationGroupStore } from '@/stores/LocationGroup';

import LocationGroupAutocompleteInput from '@rygen/rygen-ui/components/location/LocationGroupAutocompleteInput.vue';

const modelValue = defineModel({
  type: Array as PropType<string[]>,
  default: () => [],
});

const emit = defineEmits<{
  (event: 'saving', saving: boolean): void;
}>();

const props = defineProps({
  id: {
    type: String,
    default: undefined,
  },
  errors: {
    type: Array as PropType<ErrorObject[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});
const { id, errors, disabled } = toRefs(props);

const {
  locationGroups,
  loadLocationGroups,
  createLocationGroup,
  resolveLocationGroups,
} = useLocationGroupStore();

const saving = ref(false);

const selectedGroups = computed<LocationGroup[]>({
  get() {
    return resolveLocationGroups(modelValue.value ?? []);
  },
  set(next) {
    modelValue.value = next.map((g) => g.naturalId).filter(notNull);
  },
});

const onCreate = async (name: string) => {
  try {
    saving.value = true;
    emit('saving', true);

    const created = await createLocationGroup(name);

    if (created?.naturalId) {
      modelValue.value = [...(modelValue.value ?? []), created.naturalId];
    }
  } finally {
    saving.value = false;
    emit('saving', false);
  }
};

onMounted(() => {
  loadLocationGroups({ includeVisible: true });
});
</script>
